import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { FC } from 'react';
import PDFLogo from './pdf/PDFLogo';

const pdfStyles = StyleSheet.create({
  page: {
    padding: 40,
    backgroundColor: '#ffffff',
  },
  headerBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: 20,
    backgroundColor: '#f3f4f6',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    marginTop: 30,
    color: '#111827',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 15,
    color: '#4b5563',
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#1f2937',
  },
  text: {
    fontSize: 12,
    lineHeight: 1.5,
    marginBottom: 8,
    color: '#374151',
  },
  table: {
    marginVertical: 15,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    paddingVertical: 8,
  },
  tableHeader: {
    backgroundColor: '#f9fafb',
    fontWeight: 'bold',
  },
  tableCell: {
    flex: 1,
    padding: 8,
    fontSize: 11,
  },
  tableCellWide: {
    flex: 2,
    padding: 8,
    fontSize: 11,
  },
  exampleBox: {
    backgroundColor: '#f9fafb',
    padding: 15,
    marginVertical: 15,
    borderRadius: 4,
  },
  exampleTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#1f2937',
  },
  recommendedUses: {
    marginTop: 20,
  },
  bulletPoint: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  bullet: {
    width: 15,
    fontSize: 12,
  },
  bulletText: {
    flex: 1,
    fontSize: 12,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: '#6b7280',
    fontSize: 10,
  },
});

export const SmartGoalsPDF: FC = () => (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      <View style={pdfStyles.headerBar}>
        <PDFLogo width={90} height={22} />
      </View>

      <Text style={pdfStyles.title}>SMART Goals for Nonprofits</Text>
      <Text style={pdfStyles.subtitle}>A Framework for Setting Clear, Actionable, and Measurable Goals</Text>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.sectionTitle}>What Are SMART Goals?</Text>
        <Text style={pdfStyles.text}>
          SMART is a goal-setting framework that helps organizations create clear, trackable, and motivating objectives. 
          It ensures every goal is designed with structure and intentionality.
        </Text>

        <View style={pdfStyles.table}>
          <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
            <Text style={pdfStyles.tableCell}>Letter</Text>
            <Text style={pdfStyles.tableCell}>Stands for</Text>
            <Text style={pdfStyles.tableCellWide}>Description</Text>
          </View>
          <View style={pdfStyles.tableRow}>
            <Text style={pdfStyles.tableCell}>S</Text>
            <Text style={pdfStyles.tableCell}>Specific</Text>
            <Text style={pdfStyles.tableCellWide}>Clearly defines what is to be achieved. Avoids vague language.</Text>
          </View>
          <View style={pdfStyles.tableRow}>
            <Text style={pdfStyles.tableCell}>M</Text>
            <Text style={pdfStyles.tableCell}>Measurable</Text>
            <Text style={pdfStyles.tableCellWide}>Includes indicators or metrics to track progress and success.</Text>
          </View>
          <View style={pdfStyles.tableRow}>
            <Text style={pdfStyles.tableCell}>A</Text>
            <Text style={pdfStyles.tableCell}>Achievable</Text>
            <Text style={pdfStyles.tableCellWide}>Is realistic and attainable with available resources and capacity.</Text>
          </View>
          <View style={pdfStyles.tableRow}>
            <Text style={pdfStyles.tableCell}>R</Text>
            <Text style={pdfStyles.tableCell}>Relevant</Text>
            <Text style={pdfStyles.tableCellWide}>Aligns with your mission, values, and current priorities.</Text>
          </View>
          <View style={pdfStyles.tableRow}>
            <Text style={pdfStyles.tableCell}>T</Text>
            <Text style={pdfStyles.tableCell}>Time-Bound</Text>
            <Text style={pdfStyles.tableCellWide}>Has a defined timeline, deadline, or review period.</Text>
          </View>
        </View>
      </View>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.sectionTitle}>Why Nonprofits Should Use SMART Goals</Text>
        
        <View style={pdfStyles.bulletPoint}>
          <Text style={pdfStyles.bullet}>•</Text>
          <Text style={pdfStyles.bulletText}>Improves Clarity and Focus: Eliminates ambiguity in project and program design.</Text>
        </View>
        <View style={pdfStyles.bulletPoint}>
          <Text style={pdfStyles.bullet}>•</Text>
          <Text style={pdfStyles.bulletText}>Strengthens Accountability: Makes responsibilities and benchmarks clear for all team members.</Text>
        </View>
        <View style={pdfStyles.bulletPoint}>
          <Text style={pdfStyles.bullet}>•</Text>
          <Text style={pdfStyles.bulletText}>Supports Evaluation and Reporting: Easier to track outcomes and share results with funders, boards, and stakeholders.</Text>
        </View>
        <View style={pdfStyles.bulletPoint}>
          <Text style={pdfStyles.bullet}>•</Text>
          <Text style={pdfStyles.bulletText}>Promotes Strategic Alignment: Ensures individual or team goals support broader organizational strategies.</Text>
        </View>
        <View style={pdfStyles.bulletPoint}>
          <Text style={pdfStyles.bullet}>•</Text>
          <Text style={pdfStyles.bulletText}>Encourages Staff Engagement and Motivation: Tangible goals help staff see progress and feel ownership.</Text>
        </View>
      </View>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.sectionTitle}>Example SMART Goal</Text>
        
        <View style={pdfStyles.exampleBox}>
          <Text style={pdfStyles.text}>Vague Goal:</Text>
          <Text style={[pdfStyles.text, { marginLeft: 20 }]}>
            "Improve volunteer management."
          </Text>
          
          <Text style={[pdfStyles.text, { marginTop: 10 }]}>SMART Goal:</Text>
          <Text style={[pdfStyles.text, { marginLeft: 20 }]}>
            "By December 2025, implement a new volunteer management system and train 100% of program staff, reducing volunteer scheduling errors by 50%."
          </Text>
        </View>

        <View style={pdfStyles.table}>
          <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
            <Text style={pdfStyles.tableCell}>SMART Element</Text>
            <Text style={pdfStyles.tableCellWide}>Alignment</Text>
          </View>
          <View style={pdfStyles.tableRow}>
            <Text style={pdfStyles.tableCell}>Specific</Text>
            <Text style={pdfStyles.tableCellWide}>New system, staff training, reduced errors</Text>
          </View>
          <View style={pdfStyles.tableRow}>
            <Text style={pdfStyles.tableCell}>Measurable</Text>
            <Text style={pdfStyles.tableCellWide}>100% staff trained, 50% error reduction</Text>
          </View>
          <View style={pdfStyles.tableRow}>
            <Text style={pdfStyles.tableCell}>Achievable</Text>
            <Text style={pdfStyles.tableCellWide}>Based on current tech readiness</Text>
          </View>
          <View style={pdfStyles.tableRow}>
            <Text style={pdfStyles.tableCell}>Relevant</Text>
            <Text style={pdfStyles.tableCellWide}>Supports program reliability and community trust</Text>
          </View>
          <View style={pdfStyles.tableRow}>
            <Text style={pdfStyles.tableCell}>Time-Bound</Text>
            <Text style={pdfStyles.tableCellWide}>Completion by December 2025</Text>
          </View>
        </View>
      </View>

      <View style={pdfStyles.recommendedUses}>
        <Text style={pdfStyles.sectionTitle}>Recommended Uses</Text>
        <View style={pdfStyles.bulletPoint}>
          <Text style={pdfStyles.bullet}>•</Text>
          <Text style={pdfStyles.bulletText}>Strategic Planning</Text>
        </View>
        <View style={pdfStyles.bulletPoint}>
          <Text style={pdfStyles.bullet}>•</Text>
          <Text style={pdfStyles.bulletText}>Program Development and Evaluation</Text>
        </View>
        <View style={pdfStyles.bulletPoint}>
          <Text style={pdfStyles.bullet}>•</Text>
          <Text style={pdfStyles.bulletText}>Grant Reporting & Compliance</Text>
        </View>
        <View style={pdfStyles.bulletPoint}>
          <Text style={pdfStyles.bullet}>•</Text>
          <Text style={pdfStyles.bulletText}>Capacity Building Initiatives</Text>
        </View>
        <View style={pdfStyles.bulletPoint}>
          <Text style={pdfStyles.bullet}>•</Text>
          <Text style={pdfStyles.bulletText}>Staff and Board Goal Setting</Text>
        </View>
      </View>

      <Text style={pdfStyles.footer}>Copyright @ 2025 ViaTelos. All Rights Reserved.</Text>
    </Page>
  </Document>
);
